import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail, Phone, Instagram, MapPin, ArrowUpRight } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { FadeUp } from "../components/Motion";
import { api, formatApiErrorDetail } from "../lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "General enquiry", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/queries", form);
      toast.success("Thank you — your message has been sent.");
      setForm({ name: "", email: "", subject: "General enquiry", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const details = [
    { icon: Mail, label: "Email", value: "hello@arkaari.com", href: "mailto:hello@arkaari.com" },
    { icon: Phone, label: "Phone", value: "+91 00000 00000", href: "tel:+910000000000" },
    { icon: Instagram, label: "Instagram", value: "@arkaari", href: "https://instagram.com" },
    { icon: MapPin, label: "Atelier", value: "By appointment · India", href: null },
  ];

  return (
    <>
      <PageHeader
        overline="Contact"
        title={["Let's", "talk."]}
        subtitle="Reach the Arkaari atelier directly, or drop a message below. Placeholder contact details — share yours anytime and we'll update them."
        dark
      />
      <section className="max-w-[1500px] mx-auto px-5 sm:px-8 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <FadeUp>
              <div className="space-y-px border-t border-[#D5D0C5]">
                {details.map((d) => (
                  <div key={d.label} className="py-7 border-b border-[#D5D0C5] flex items-center gap-5">
                    <d.icon size={20} strokeWidth={1.5} className="text-[#C6A15B]" />
                    <div className="flex-1">
                      <p className="overline text-[#7a7568]">{d.label}</p>
                      {d.href ? (
                        <a data-testid={`contact-${d.label.toLowerCase()}`} href={d.href} target="_blank" rel="noreferrer" className="font-serif text-2xl font-light link-underline">{d.value}</a>
                      ) : (
                        <p className="font-serif text-2xl font-light">{d.value}</p>
                      )}
                    </div>
                    {d.href && <ArrowUpRight size={18} strokeWidth={1.5} className="text-[#7a7568]" />}
                  </div>
                ))}
              </div>
            </FadeUp>
          </div>

          <div className="lg:col-span-7">
            <form data-testid="contact-form" onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-8">
              <label className="block"><span className="overline text-[#7a7568] block mb-2">Name *</span><input data-testid="contact-name" className="field" value={form.name} onChange={set("name")} placeholder="Your name" /></label>
              <label className="block"><span className="overline text-[#7a7568] block mb-2">Email *</span><input data-testid="contact-email" type="email" className="field" value={form.email} onChange={set("email")} placeholder="you@email.com" /></label>
              <label className="block sm:col-span-2"><span className="overline text-[#7a7568] block mb-2">Subject</span><input data-testid="contact-subject" className="field" value={form.subject} onChange={set("subject")} /></label>
              <label className="block sm:col-span-2"><span className="overline text-[#7a7568] block mb-2">Message *</span><textarea data-testid="contact-message" rows={6} className="field resize-none" value={form.message} onChange={set("message")} placeholder="How can we help?" /></label>
              <div className="sm:col-span-2">
                <button data-testid="submit-contact-btn" disabled={loading} className="btn-gold px-10 py-4 overline disabled:opacity-60">
                  <span className="inline-flex items-center gap-2">{loading && <Loader2 size={16} className="animate-spin" />}{loading ? "Sending..." : "Send message"}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}
